import React, { useState, useEffect } from 'react'
import { 
  Users, Search, Filter, Mail, Phone, MapPin, Calendar, 
  Eye, Edit, UserPlus, Download, AlertCircle, CheckCircle,
  Baby, Building, User
} from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface Parent {
  id: string
  email: string
  nom?: string
  prenom?: string
  adresse?: string
  cpostal?: string
  localite?: string
  telephone?: string
  telephone2?: string
  nnational?: string
  newsletter: boolean
  account_type: 'parent' | 'organisation' | 'other'
  organisation_name?: string
  company_number?: string
  is_legal_guardian: boolean
  has_new_registration_notification: boolean
  created_at: string
  kids_count?: number
  active_registrations_count?: number
}

export function ParentsManagement() {
  const [parents, setParents] = useState<Parent[]>([])
  const [filteredParents, setFilteredParents] = useState<Parent[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [currentOffset, setCurrentOffset] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedParent, setSelectedParent] = useState<Parent | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [chunkSize] = useState(500) // Charger 500 parents par chunk
  const [filters, setFilters] = useState({
    accountType: '',
    hasKids: false,
    newsletter: false,
    city: ''
  })

  useEffect(() => {
    fetchAllParents() // Chargement automatique de toutes les données
  }, [])

  useEffect(() => {
    applyFilters()
  }, [parents, searchTerm, filters])

  const fetchAllParents = async () => {
    setLoading(true)
    let allParents: Parent[] = []
    let offset = 0
    let hasMoreData = true

    try {
      console.log('🔄 Starting automatic loading of all parents...')
      
      while (hasMoreData) {
        console.log(`📦 Fetching parents chunk: offset ${offset}, limit ${chunkSize}`)
        
        const { data, error } = await supabase
          .from('users')
          .select(`
            *,
            kids(id, nom, prenom),
            registrations:registrations!user_id(id, payment_status)
          `)
          .eq('role', 'parent')
          .range(offset, offset + chunkSize - 1)
          .order('nom', { ascending: true })

        if (error) throw error
        
        console.log(`📊 Parents chunk received: ${data?.length || 0} items`)

        const parentsWithCounts = data?.map(parent => ({
          ...parent,
          kids_count: parent.kids?.length || 0,
          active_registrations_count: parent.registrations?.filter(r => r.payment_status !== 'cancelled').length || 0
        })) || []

        // Ajouter ce chunk aux données totales
        allParents = [...allParents, ...parentsWithCounts]
        
        // Si on a reçu moins d'éléments que demandé, on a atteint la fin
        if (!data || data.length < chunkSize) {
          hasMoreData = false
          console.log('✅ All parents loaded successfully')
        } else {
          offset += chunkSize
          // Petite pause entre les requêtes pour éviter de surcharger Supabase
          await new Promise(resolve => setTimeout(resolve, 100))
        }
      }
      
      // Mettre à jour l'état avec toutes les données
      setParents(allParents)
      setHasMore(false)
      console.log(`🎉 Total parents loaded: ${allParents.length}`)

    } catch (err) {
      console.error('❌ Error fetching all parents:', err)
    } finally {
      setLoading(false)
    }
  }


  const applyFilters = () => {
    let filtered = parents

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(parent => 
        `${parent.prenom} ${parent.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.telephone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.localite?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parent.organisation_name?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Account type filter
    if (filters.accountType) {
      filtered = filtered.filter(parent => parent.account_type === filters.accountType)
    }

    // Has kids filter
    if (filters.hasKids) {
      filtered = filtered.filter(parent => (parent.kids_count || 0) > 0)
    }

    // Newsletter filter
    if (filters.newsletter) {
      filtered = filtered.filter(parent => parent.newsletter)
    }

    // City filter
    if (filters.city) {
      filtered = filtered.filter(parent => 
        parent.localite?.toLowerCase().includes(filters.city.toLowerCase())
      )
    }

    setFilteredParents(filtered)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  const getAccountTypeLabel = (type: string) => {
    switch (type) {
      case 'parent': return 'Parent'
      case 'organisation': return 'Organisation'
      case 'other': return 'Autre'
      default: return type
    }
  }

  const getAccountTypeBadge = (type: string) => {
    switch (type) {
      case 'parent':
        return <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">Parent</span>
      case 'organisation':
        return <span className="px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">Organisation</span>
      case 'other':
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">Autre</span>
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{type}</span>
    }
  }

  const resetFilters = () => {
    setFilters({
      accountType: '',
      hasKids: false,
      newsletter: false,
      city: ''
    })
    setSearchTerm('')
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Parents</h1>
        </div>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Parents</h1>
          <p className="text-gray-600">Tous les utilisateurs avec le rôle parent</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-blue-600 text-white' : 'bg-gray-600 text-white hover:bg-gray-700'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtres</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <Download className="h-4 w-4" />
            <span>Exporter</span>
          </button>
          <button className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            <UserPlus className="h-4 w-4" />
            <span>Nouveau parent</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, email, téléphone, ville ou organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 pt-4 border-t border-gray-200">
              <div>
                <select
                  value={filters.accountType}
                  onChange={(e) => setFilters(prev => ({ ...prev, accountType: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tous les types</option>
                  <option value="parent">Parent</option>
                  <option value="organisation">Organisation</option>
                  <option value="other">Autre</option>
                </select>
              </div>
              
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.hasKids}
                    onChange={(e) => setFilters(prev => ({ ...prev, hasKids: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">A des enfants</span>
                </label>
              </div>
              
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={filters.newsletter}
                    onChange={(e) => setFilters(prev => ({ ...prev, newsletter: e.target.checked }))}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Newsletter</span>
                </label>
              </div>
              
              <div>
                <input
                  type="text"
                  placeholder="Ville..."
                  value={filters.city}
                  onChange={(e) => setFilters(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div className="flex space-x-2">
                <button
                  onClick={resetFilters}
                  className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total parents</p>
              <p className="text-2xl font-bold text-gray-900">{filteredParents.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <Baby className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avec enfants</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredParents.filter(parent => (parent.kids_count || 0) > 0).length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Building className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Organisations</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredParents.filter(parent => parent.account_type === 'organisation').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Mail className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Newsletter</p>
              <p className="text-2xl font-bold text-gray-900">
                {filteredParents.filter(parent => parent.newsletter).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Parents List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {/* Results Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{filteredParents.length}</span> parents trouvés
            </div>
            <div className="text-sm text-gray-500">
              <span>Total chargé: {parents.length}</span>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Enfants
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inscriptions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredParents.map((parent) => (
                <tr key={parent.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-500" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {parent.prenom} {parent.nom}
                        </div>
                        <div className="text-sm text-gray-500">
                          {parent.email}
                        </div>
                        {parent.organisation_name && (
                          <div className="text-sm text-gray-500">
                            {parent.organisation_name}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parent.telephone && (
                        <div className="flex items-center">
                          <Phone className="h-4 w-4 mr-1 text-gray-400" />
                          {parent.telephone}
                        </div>
                      )}
                      {parent.localite && (
                        <div className="flex items-center mt-1">
                          <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                          {parent.cpostal} {parent.localite}
                        </div>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getAccountTypeBadge(parent.account_type)}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {parent.kids_count || 0} enfant{(parent.kids_count || 0) !== 1 ? 's' : ''}
                    </span>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {parent.active_registrations_count || 0} actives
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {parent.newsletter && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                          Newsletter
                        </span>
                      )}
                      {parent.is_legal_guardian && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                          Tuteur légal
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => setSelectedParent(parent)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-gray-600 hover:text-gray-900">
                      <Edit className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredParents.length === 0 && !loading && parents.length > 0 && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun parent trouvé</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun parent ne correspond aux critères de recherche.
          </p>
        </div>
      )}

      {parents.length === 0 && !loading && (
        <div className="text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun parent dans la base de données</h3>
          <p className="mt-1 text-sm text-gray-500">
            Aucun parent n'a été trouvé dans la base de données.
          </p>
        </div>
      )}

      {/* Parent Detail Modal */}
      {selectedParent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">
                Profil de {selectedParent.prenom} {selectedParent.nom}
              </h3>
              <button
                onClick={() => setSelectedParent(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <User className="h-4 w-4 mr-2" />
                    Informations personnelles
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Nom complet:</strong> {selectedParent.prenom} {selectedParent.nom}</div>
                    <div><strong>Email:</strong> {selectedParent.email}</div>
                    <div><strong>Type de compte:</strong> {getAccountTypeLabel(selectedParent.account_type)}</div>
                    {selectedParent.organisation_name && (
                      <div><strong>Organisation:</strong> {selectedParent.organisation_name}</div>
                    )}
                    {selectedParent.company_number && (
                      <div><strong>Numéro d'entreprise:</strong> {selectedParent.company_number}</div>
                    )}
                    <div><strong>Numéro national:</strong> {selectedParent.nnational || 'Non renseigné'}</div>
                    <div><strong>Inscrit le:</strong> {formatDate(selectedParent.created_at)}</div>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Phone className="h-4 w-4 mr-2" />
                    Contact
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Téléphone:</strong> {selectedParent.telephone || 'Non renseigné'}</div>
                    <div><strong>Téléphone 2:</strong> {selectedParent.telephone2 || 'Non renseigné'}</div>
                    <div><strong>Adresse:</strong> {selectedParent.adresse ? `${selectedParent.adresse}, ${selectedParent.cpostal} ${selectedParent.localite}` : 'Non renseignée'}</div>
                  </div>
                </div>
              </div>

              {/* Status & Settings */}
              <div className="space-y-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Statut et préférences
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Newsletter:</strong> {selectedParent.newsletter ? 'Oui' : 'Non'}</div>
                    <div><strong>Tuteur légal:</strong> {selectedParent.is_legal_guardian ? 'Oui' : 'Non'}</div>
                    <div><strong>Notifications nouvelles inscriptions:</strong> {selectedParent.has_new_registration_notification ? 'Oui' : 'Non'}</div>
                  </div>
                </div>

                {/* Statistics */}
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                    <Baby className="h-4 w-4 mr-2" />
                    Statistiques
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div><strong>Nombre d'enfants:</strong> {selectedParent.kids_count || 0}</div>
                    <div><strong>Inscriptions actives:</strong> {selectedParent.active_registrations_count || 0}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setSelectedParent(null)}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Fermer
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Modifier
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}